import { useLocale } from "../../hooks/useLocale";
import { msg } from "../../locales/translate";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { ArrowRight, Banknote, Building2, ChevronDown, CircleCheck, Coins, CreditCard, Gauge, Globe2, Info, Network, OctagonAlert, ShieldAlert, ShieldCheck, ShieldX, Store, TriangleAlert, Undo2, UserRound, Wallet, type LucideIcon } from "lucide-react";
import { emailActiveDays, emailAmountFields, emailIdentityFields, emailInfoDate, emailLowConversion, emailShare, emailSignalLimits, emailSignals, emailVerdict, type EmailAmounts, type EmailCard, type EmailInfoData, type EmailSignal } from "./emailInfoData";

type Seen = { first_seen: string | null; last_seen: string | null };
type Tone = "ok" | "warn" | "danger" | "unknown";
type Part = { key: string; label: string; color: string; value: number | null; text: string; share: number | null };
type Outcome = { key: string; label: string; text: string; share: number | null; tone: Tone };

/** Completed and failed differ in lightness too, so the pair stays apart for colour-blind readers. */
const colors = { completed: "#18774d", failed: "#ee8597", abandoned: "#b4bcc8", other: "#dfe3e8" };

/** Section headings match the uppercase labels of the rule details popup. */
export const SectionLabel = styled.h3`
	display: flex; align-items: baseline; flex-wrap: wrap; gap: 4px 10px; margin: 26px 0 10px; color: #667085;
	font-size: 11px; font-weight: 600; letter-spacing: .04em; line-height: 1.4; text-transform: uppercase;
	.count { color: #98a2b3; }
	.note { color: #98a2b3; font-size: 12px; font-weight: 400; letter-spacing: 0; text-transform: none; }
`;

const Content = styled.div`
	min-width: 0; color: #344054; font-size: 13px; line-height: 1.5;
	strong { font-weight: 600; color: #182230; }
	p { margin: 0; }
	ul { margin: 0; padding: 0; list-style: none; }
	.hint { color: #667085; font-size: 12px; }
	.muted { color: #98a2b3; font-weight: 400; }
	.card { min-width: 0; border: 1px solid #d0d5dd; border-radius: 10px; background: #fff; box-shadow: 0 1px 2px rgba(16, 24, 40, .05); }
	.card.empty { padding: 14px 18px; color: #667085; }

	/* The verdict uses the tones of rule summaries: red blocks, amber warns, green allows. */
	.verdict { display: flex; gap: 12px; padding: 16px 18px; border: 1px solid #d0d5dd; border-radius: 10px; background: #fff; box-shadow: 0 1px 2px rgba(16, 24, 40, .05); }
	.verdict > svg { flex-shrink: 0; margin-top: 1px; color: #667085; }
	.verdict.danger { background: #fdf3f5; border-color: #eeb4c2; }
	.verdict.warn { background: #fdf8ee; border-color: #ebcd8f; }
	.verdict.ok { background: #f0f9f4; border-color: #a9d9be; }
	.verdict.danger > svg, .verdict.danger .headline { color: #a92340; }
	.verdict.warn > svg, .verdict.warn .headline { color: #925b10; }
	.verdict.ok > svg, .verdict.ok .headline { color: #18774d; }
	.verdict-text { flex: 1; min-width: 0; }
	.headline { font-size: 15px; font-weight: 600; line-height: 1.45; color: #182230; }
	.verdict-text .hint { margin-top: 4px; }
	.signals { display: flex; flex-wrap: wrap; gap: 6px 8px; margin-top: 10px; }
	.signal { display: inline-flex; align-items: center; gap: 6px; padding: 3px 10px; border: 1px solid; border-radius: 999px; background: #fff; font-size: 12px; font-weight: 500; line-height: 18px; }
	.signal.danger { color: #a92340; border-color: #eeb4c2; }
	.signal.warn { color: #925b10; border-color: #ebcd8f; }

	.kpis { display: grid; grid-template-columns: repeat(auto-fit, minmax(min(100%, 300px), 1fr)); gap: 16px; }
	.kpi { display: flex; flex-direction: column; padding: 16px 18px 14px; }
	.kpi-head { display: flex; align-items: center; flex-wrap: wrap; gap: 8px; color: #475467; font-weight: 500; }
	.kpi-head > svg { flex-shrink: 0; color: #98a2b3; }
	.kpi-head .aside { margin-left: auto; color: #667085; font-size: 12px; font-weight: 400; }
	.currency { padding: 0 6px; border: 1px solid #d0d5dd; border-radius: 6px; background: #f9fafb; color: #344054; font-size: 11px; font-weight: 600; letter-spacing: .04em; line-height: 18px; }
	.hero { display: flex; align-items: baseline; flex-wrap: wrap; gap: 4px 8px; margin: 8px 0 12px; }
	.hero-value { font-size: 30px; font-weight: 600; line-height: 1.15; color: #182230; overflow-wrap: anywhere; }
	.hero-unit { font-size: 15px; font-weight: 600; color: #475467; }
	.hero-note { color: #667085; }
	/* Segments are separated by a 2px gap rather than outlines; a tiny share keeps a visible sliver. */
	.bar { display: flex; gap: 2px; height: 10px; border-radius: 5px; overflow: hidden; background: #eaecf0; }
	.bar > span { flex-basis: 0; min-width: 4px; }
	.legend { display: grid; gap: 1px; margin-top: 12px; }
	.line { display: grid; grid-template-columns: 16px minmax(0, 1fr) auto 48px; align-items: center; column-gap: 8px; min-height: 26px; }
	.line > svg { justify-self: center; }
	.dot { justify-self: center; width: 8px; height: 8px; border-radius: 50%; }
	.value { font-weight: 600; color: #182230; text-align: right; font-variant-numeric: tabular-nums; white-space: nowrap; }
	.share { color: #667085; font-size: 12px; text-align: right; font-variant-numeric: tabular-nums; white-space: nowrap; }
	.after { display: grid; gap: 2px; margin-top: 12px; padding-top: 10px; border-top: 1px solid #eaecf0; }
	.after-head { display: flex; justify-content: space-between; gap: 8px; margin-bottom: 2px; color: #667085; font-size: 11px; font-weight: 600; letter-spacing: .04em; text-transform: uppercase; }
	.after-head span { font-weight: 400; letter-spacing: 0; text-transform: none; }
	.outcome { margin: 0 -8px; padding: 0 8px; border-radius: 6px; }
	.outcome.ok > svg { color: #18774d; }
	.outcome.warn { background: #fdf8ee; }
	.outcome.warn > svg { color: #925b10; }
	.outcome.danger { background: #fdf3f5; }
	.outcome.danger > svg { color: #a92340; }
	.average { display: flex; justify-content: space-between; flex-wrap: wrap; gap: 4px 12px; margin-top: 10px; padding-top: 10px; border-top: 1px dashed #eaecf0; color: #667085; }
	.average strong { font-variant-numeric: tabular-nums; }

	.linked { padding: 16px 18px; }
	.tiles { display: grid; grid-template-columns: repeat(auto-fill, minmax(min(100%, 190px), 1fr)); gap: 10px; }
	.tile { position: relative; display: flex; align-items: center; gap: 10px; min-width: 0; padding: 10px 12px; border: 1px solid #eaecf0; border-radius: 8px; background: #f9fafb; }
	.tile-icon { display: inline-flex; align-items: center; justify-content: center; flex: 0 0 32px; height: 32px; border: 1px solid #eaecf0; border-radius: 8px; background: #fff; color: #667085; }
	.tile-text { display: flex; flex-direction: column; min-width: 0; }
	.tile-text strong { font-size: 18px; line-height: 1.25; }
	.tile-text span { color: #667085; font-size: 12px; overflow-wrap: anywhere; }
	.tile.flagged { border-color: #ebcd8f; background: #fdf8ee; }
	.tile.flagged .tile-icon { border-color: #ebcd8f; color: #925b10; }
	.tile-flag { position: absolute; top: 8px; right: 8px; color: #925b10; }
	/* Label and value share one 26px line, as in the rule breakdown. */
	.recipe { display: grid; grid-template-columns: 150px minmax(0, 1fr); align-items: baseline; gap: 8px 16px; margin: 16px 0 0; padding-top: 14px; border-top: 1px solid #eaecf0; line-height: 26px; }
	.recipe.plain { margin: 0; padding: 0; border: 0; }
	.recipe dt { color: #667085; font-size: 12px; }
	.recipe dd { margin: 0; min-width: 0; color: #182230; overflow-wrap: anywhere; }
	.chips { display: flex; flex-wrap: wrap; align-items: baseline; gap: 6px 8px; }
	.chip { max-width: 100%; padding: 2px 8px; border: 1px solid #d0d5dd; border-radius: 6px; background: #f9fafb; line-height: 20px; overflow-wrap: anywhere; }
	.range { display: inline-flex; flex-wrap: wrap; align-items: center; column-gap: 8px; }
	.range > svg { flex-shrink: 0; color: #98a2b3; }

	.stack { display: grid; gap: 12px; }
	.list { overflow: hidden; }
	.list-head { display: flex; align-items: center; flex-wrap: wrap; gap: 4px 8px; padding: 12px 16px; border-bottom: 1px solid #eaecf0; background: #f9fafb; overflow-wrap: anywhere; }
	.list-head > svg { flex-shrink: 0; color: #98a2b3; }
	.list-head .aside { margin-left: auto; color: #667085; font-size: 12px; }
	.list > p { padding: 12px 16px; color: #667085; }
	.item + .item { border-top: 1px solid #eaecf0; }
	.item-head { display: flex; align-items: center; flex-wrap: wrap; gap: 8px 14px; padding: 12px 16px; list-style: none; }
	summary.item-head { cursor: pointer; }
	summary.item-head::-webkit-details-marker { display: none; }
	summary.item-head:hover { background: #fafbfc; }
	summary.item-head:focus-visible { outline: 2px solid #84adff; outline-offset: -2px; }
	.item-icon { flex-shrink: 0; color: #98a2b3; }
	.item-title { display: flex; flex-direction: column; flex: 1 1 220px; min-width: 0; }
	.item-name { display: flex; align-items: baseline; flex-wrap: wrap; gap: 2px 8px; overflow-wrap: anywhere; }
	.item-sub { color: #667085; font-size: 12px; }
	.scheme { padding: 0 6px; border-radius: 4px; background: #eff4ff; color: #1849a9; font-size: 11px; font-weight: 700; letter-spacing: .04em; line-height: 18px; }
	.mini { display: inline-flex; align-items: center; gap: 8px; flex: 0 0 auto; white-space: nowrap; font-variant-numeric: tabular-nums; }
	.mini-bar { width: 64px; height: 6px; border-radius: 3px; background: #eaecf0; overflow: hidden; }
	.mini-bar > span { display: block; height: 100%; border-radius: 3px; background: ${colors.completed}; }
	.stat { color: #475467; white-space: nowrap; }
	.badge { display: inline-flex; align-items: center; gap: 4px; padding: 1px 8px; border: 1px solid; border-radius: 999px; font-size: 12px; font-weight: 500; white-space: nowrap; }
	.badge.danger { color: #a92340; border-color: #eeb4c2; background: #fdf3f5; }
	.badge.warn { color: #925b10; border-color: #ebcd8f; background: #fdf8ee; }
	.chevron { flex-shrink: 0; color: #98a2b3; transition: transform .15s; }
	details[open] > summary > .chevron { transform: rotate(180deg); }
	.item-body { padding: 4px 16px 16px 45px; }
	/* Where the browser can animate <details>, rows slide open; elsewhere they toggle instantly. */
	details.item { interpolate-size: allow-keywords; }
	details.item::details-content { block-size: 0; overflow: clip; transition: block-size .22s ease, content-visibility .22s allow-discrete; }
	details.item[open]::details-content { block-size: auto; }
	@media (prefers-reduced-motion: reduce) { details.item::details-content { transition: none; } .chevron { transition: none; } }
	.sub-label { margin: 16px 0 4px; color: #667085; font-size: 11px; font-weight: 600; letter-spacing: .04em; text-transform: uppercase; }
	.identity { margin-top: 8px; border: 1px solid #eaecf0; border-radius: 8px; }
	.identity > summary { display: flex; align-items: center; flex-wrap: wrap; gap: 4px 10px; padding: 8px 12px; cursor: pointer; list-style: none; overflow-wrap: anywhere; }
	.identity > summary::-webkit-details-marker { display: none; }
	.identity > summary:focus-visible { outline: 2px solid #84adff; outline-offset: -2px; }
	.identity > summary > svg { flex-shrink: 0; color: #98a2b3; }
	.identity > summary .item-sub { margin-left: auto; }
	.identity[open] > summary { border-bottom: 1px solid #eaecf0; }
	.fields { display: grid; grid-template-columns: repeat(auto-fit, minmax(min(100%, 200px), 1fr)); gap: 10px 20px; margin: 0; padding: 12px; }
	.fields dt { color: #667085; font-size: 12px; }
	.fields dd { margin: 2px 0 0; overflow-wrap: anywhere; }
	.fields dd.missing { color: #98a2b3; }

	@media (max-width: 600px) {
		.verdict, .kpi, .linked { padding: 14px; }
		.hero-value { font-size: 26px; }
		.recipe { grid-template-columns: minmax(0, 1fr); gap: 0; }
		.recipe dt { line-height: 1.5; }
		.recipe dd + dt { margin-top: 10px; }
		.item-body { padding: 4px 16px 16px; }
	}
`;
const TableScroll = styled.div`
	max-width: 100%; margin-top: 12px; overflow-x: auto; border: 1px solid #eaecf0; border-radius: 8px;
	&:focus-visible { outline: 2px solid #84adff; outline-offset: 2px; }
`;
const Table = styled.table`
	width: 100%; border-collapse: collapse; font-size: 12px;
	th, td { padding: 8px 12px; border-bottom: 1px solid #eaecf0; text-align: right; }
	thead th { background: #f9fafb; color: #667085; font-weight: 500; white-space: nowrap; }
	th:first-child, td:first-child { text-align: left; }
	tbody th { font-weight: 600; color: #182230; }
	td { white-space: nowrap; font-variant-numeric: tabular-nums; }
	tbody tr:last-child > * { border-bottom: 0; }
`;

const useFormat = () => {
	useLocale();
	const { t, i18n } = useTranslation();
	const language = i18n.resolvedLanguage || i18n.language;
	return {
		t, language,
		count: (value: number | null) => value === null ? "—" : value.toLocaleString(language),
		money: (value: number | null, digits = 8) => value === null ? "—" : value.toLocaleString(language, { minimumFractionDigits: 2, maximumFractionDigits: digits }),
		percent: (share: number | null) => share === null ? "—" : share.toLocaleString(language, { style: "percent", maximumFractionDigits: share > 0 && share < .1 ? 1 : 0 }),
		date: (value: string | null) => emailInfoDate(value, language),
	};
};

const outcomeTone = (value: number | null, danger: boolean): Tone => value === null ? "unknown" : value === 0 ? "ok" : danger ? "danger" : "warn";
const toneIcons: Record<Tone, LucideIcon | null> = { ok: CircleCheck, warn: TriangleAlert, danger: OctagonAlert, unknown: null };
const signalIcons: Record<EmailSignal["key"], LucideIcon> = { charged: OctagonAlert, returned: Undo2, refunded: Undo2, conversion: Gauge, cards: CreditCard, clients_count: Building2, distinct_names: UserRound, countries: Globe2 };
const verdictIcons = { danger: ShieldX, warn: ShieldAlert, ok: ShieldCheck, empty: Info };
const linkedMetrics = [["cards", CreditCard], ["mids_count", Store], ["clients_count", Building2], ["ips", Network], ["countries", Globe2], ["currencies", Coins], ["instruments", Wallet], ["distinct_names", UserRound]] as const;

/** The parts of a whole as one bar; the legend beside it carries every value as text. */
function Bar({ parts }: { parts: Part[] }) {
	useLocale();
	return <div className="bar" aria-hidden="true">{parts.filter(part => part.value).map(part => <span key={part.key} title={`${part.label}: ${part.text}`} style={{ flexGrow: part.value!, background: part.color }} />)}</div>;
}

function Legend({ parts, percent }: { parts: Part[]; percent: (share: number | null) => string }) {
	useLocale();
	return <ul className="legend">{parts.map(part => <li className="line" key={part.key}>
		<span className="dot" style={{ background: part.color }} aria-hidden="true" /><span>{part.label}</span><span className="value">{part.text}</span><span className="share">{percent(part.share)}</span>
	</li>)}</ul>;
}

/** What happened to completed payments: refunds and chargebacks, as a share of what was completed. */
function AfterCompletion({ rows }: { rows: Outcome[] }) {
	useLocale();
	const { t, percent } = useFormat();
	return <div className="after">
		<div className="after-head">{t("emailInfo.afterCompletion")}<span>{t("emailInfo.shareOfCompleted")}</span></div>
		{rows.map(row => {
			const Icon = toneIcons[row.tone];
			return <div className={`line outcome ${row.tone}`} key={row.key}>{Icon ? <Icon size={14} aria-hidden="true" /> : <span />}<span>{row.label}</span><span className="value">{row.text}</span><span className="share">{percent(row.share)}</span></div>;
		})}
	</div>;
}

function Verdict({ summary, signals }: { summary: EmailInfoData["summary"]; signals: EmailSignal[] }) {
	useLocale();
	const { t, count, percent } = useFormat();
	const tone = emailVerdict(summary, signals), Icon = verdictIcons[tone];
	const hint = (signal: EmailSignal) => signal.key === "conversion"
		? t("emailInfo.conversionHint", { rate: percent(emailLowConversion.rate), attempts: emailLowConversion.attempts })
		: signal.key in emailSignalLimits ? t("emailInfo.signalHint", { value: emailSignalLimits[signal.key as keyof typeof emailSignalLimits] }) : undefined;
	return <section className={`verdict ${tone}`} aria-label={t("emailInfo.summary")}>
		<Icon size={22} aria-hidden="true" />
		<div className="verdict-text">
			<p className="headline">{t(`emailInfo.verdict.${tone}`)}</p>
			{signals.length
				? <ul className="signals" aria-label={t("emailInfo.signalsLabel")}>{signals.map(signal => {
					const SignalIcon = signalIcons[signal.key];
					return <li className={`signal ${signal.tone}`} key={signal.key} title={hint(signal)}><SignalIcon size={13} aria-hidden="true" />{t(`emailInfo.signals.${signal.key}`, { value: signal.key === "conversion" ? percent(signal.value) : count(signal.value) })}</li>;
				})}</ul>
				: <p className="hint">{t(`emailInfo.verdictHint.${tone}`)}</p>}
		</div>
	</section>;
}

function Conversion({ summary }: { summary: EmailInfoData["summary"] }) {
	useLocale();
	const { t, count, percent } = useFormat();
	const { attempts, completed, failed, abandoned } = summary;
	const known = [completed, failed, abandoned];
	// Payments in statuses the response does not break down still take their place in the bar.
	const other = attempts !== null && known.every(value => value !== null) ? Math.max(0, attempts - known.reduce<number>((sum, value) => sum + value!, 0)) : 0;
	const part = (key: keyof typeof colors, value: number | null, label = t(`emailInfo.${key}`)): Part => ({ key, label, color: colors[key], value, text: count(value), share: emailShare(value, attempts) });
	const parts = [part("completed", completed), part("failed", failed), part("abandoned", abandoned), ...(other ? [part("other", other, t("emailInfo.otherStatuses"))] : [])];
	return <article className="card kpi" aria-label={t("emailInfo.conversion")}>
		<header className="kpi-head"><Gauge size={16} aria-hidden="true" />{t("emailInfo.conversion")}<span className="aside">{t("emailInfo.attemptsCount", { value: count(attempts) })}</span></header>
		<div className="hero"><span className="hero-value">{percent(emailShare(completed, attempts))}</span><span className="hero-note">{t("emailInfo.completedOf", { completed: count(completed), attempts: count(attempts) })}</span></div>
		<Bar parts={parts} />
		<Legend parts={parts} percent={percent} />
		<AfterCompletion rows={(["refunded", "charged", "returned"] as const).map(key => ({ key, label: t(`emailInfo.${key}`), text: count(summary[key]), share: emailShare(summary[key], completed), tone: outcomeTone(summary[key], key === "charged") }))} />
	</article>;
}

function Volume({ row, split }: { row: EmailAmounts; split: boolean }) {
	useLocale();
	const { t, count, money, percent } = useFormat();
	const total = row.completed_amount === null && row.failed_amount === null ? null : (row.completed_amount ?? 0) + (row.failed_amount ?? 0);
	const part = (key: "completed" | "failed", value: number | null): Part => ({ key, label: t(`emailInfo.${key}`), color: colors[key], value, text: money(value), share: emailShare(value, total) });
	const parts = [part("completed", row.completed_amount), part("failed", row.failed_amount)];
	const outcome = (key: "refunded_amount" | "charged_amount", label: string): Outcome => ({ key, label, text: money(row[key]), share: emailShare(row[key], row.completed_amount), tone: outcomeTone(row[key], key === "charged_amount") });
	const average = row.completed_amount !== null && row.completed ? row.completed_amount / row.completed : null;
	return <article className="card kpi" aria-label={`${t("emailInfo.volume")} · ${row.currency}`}>
		<header className="kpi-head"><Banknote size={16} aria-hidden="true" />{t("emailInfo.volume")}<span className="currency">{row.currency}</span>
			{split && <span className="aside">{t("emailInfo.completedOf", { completed: count(row.completed), attempts: count(row.attempts) })}</span>}</header>
		<div className="hero"><span className="hero-value">{money(row.completed_amount)}</span><span className="hero-unit">{row.currency}</span><span className="hero-note">{t("emailInfo.completedVolume")}</span></div>
		<Bar parts={parts} />
		<Legend parts={parts} percent={percent} />
		<AfterCompletion rows={[outcome("refunded_amount", t("emailInfo.refunded")), outcome("charged_amount", t("emailInfo.charged"))]} />
		<div className="average">{t("emailInfo.averagePayment")}<strong>{average === null ? "—" : `${money(average, 2)} ${row.currency}`}</strong></div>
	</article>;
}

function SeenRange({ seen, zone = false }: { seen: Seen; zone?: boolean }) {
	useLocale();
	const { t, date } = useFormat();
	const days = emailActiveDays(seen);
	return <span className="range">{date(seen.first_seen)}<ArrowRight size={14} role="img" aria-label={t("emailInfo.to")} />{date(seen.last_seen)}{zone && " UTC"}{days !== null && <span className="hint">· {t("emailInfo.activeDays", { value: days })}</span>}</span>;
}

function MiniRate({ completed, attempts }: { completed: number | null; attempts: number | null }) {
	useLocale();
	const { t, count, percent } = useFormat();
	const share = emailShare(completed, attempts);
	return <span className="mini" title={`${t("emailInfo.conversion")}: ${percent(share)}`}>
		<span className="mini-bar" aria-hidden="true"><span style={{ width: `${Math.min(share ?? 0, 1) * 100}%` }} /></span>
		<strong>{percent(share)}</strong><span className="hint">{count(completed)}/{count(attempts)}</span>
	</span>;
}

function Disputes({ charged, returned }: { charged: number | null; returned?: number | null }) {
	useLocale();
	const { t, count } = useFormat();
	return <>
		{!!charged && <span className="badge danger"><OctagonAlert size={12} aria-hidden="true" />{t("emailInfo.signals.charged", { value: count(charged) })}</span>}
		{!!returned && <span className="badge warn"><Undo2 size={12} aria-hidden="true" />{t("emailInfo.signals.returned", { value: count(returned) })}</span>}
	</>;
}

function Amounts({ rows }: { rows: EmailAmounts[] }) {
	useLocale();
	const { t, count, money } = useFormat();
	const keys = ["attempts", "completed", ...emailAmountFields] as const;
	if (!rows.length) return <p className="hint">{t("emailInfo.noAmounts")}</p>;
	return <TableScroll role="region" aria-label={t("emailInfo.amounts")} tabIndex={0}>
		<Table aria-label={t("emailInfo.amounts")}>
			<thead><tr><th scope="col">{t("emailInfo.currency")}</th>{keys.map(key => <th key={key} scope="col">{t(`emailInfo.${key}`)}</th>)}</tr></thead>
			<tbody>{rows.map((row, index) => <tr key={`${row.currency}:${index}`}><th scope="row">{row.currency}</th>{keys.map(key => <td key={key}>{key.endsWith("_amount") ? money(row[key]) : count(row[key])}</td>)}</tr>)}</tbody>
		</Table>
	</TableScroll>;
}

function Clients({ clients }: { clients: EmailInfoData["clients"] }) {
	useLocale();
	const { t, count, date } = useFormat();
	if (!clients.length) return <p className="card empty">{t("emailInfo.noClients")}</p>;
	const single = clients.length === 1 && clients[0].mids.length === 1;
	return <div className="stack">{clients.map((client, clientIndex) => <section className="card list" key={`${client.id}:${clientIndex}`} aria-label={client.name || t("emailInfo.unnamedClient")}>
		<header className="list-head"><Building2 size={16} aria-hidden="true" /><strong>{client.name || t("emailInfo.unnamedClient")}</strong>{client.id && <span className="muted">#{client.id}</span>}<span className="aside">{t("emailInfo.midsCount", { value: count(client.mids.length) })}</span></header>
		{!client.mids.length && <p>{t("emailInfo.noMids")}</p>}
		{client.mids.map((mid, midIndex) => <details className="item" key={`${mid.id}:${midIndex}`} open={single || undefined}>
			<summary className="item-head">
				<Store className="item-icon" size={16} aria-hidden="true" />
				<span className="item-title"><span className="item-name"><strong>{mid.name || msg("mid")}</strong>{mid.id && <span className="muted">#{mid.id}</span>}</span><span className="item-sub">{t("emailInfo.last_seen")}: {date(mid.last_seen)} UTC</span></span>
				<MiniRate completed={mid.completed} attempts={mid.attempts} />
				<Disputes charged={mid.charged} returned={mid.returned} />
				<ChevronDown className="chevron" size={16} aria-hidden="true" />
			</summary>
			<div className="item-body">
				<dl className="recipe plain"><dt>{t("emailInfo.activity")}</dt><dd><SeenRange seen={mid} /></dd></dl>
				<Amounts rows={mid.amounts} />
				<div className="sub-label">{t("emailInfo.identities")}</div>
				<p className="hint">{t("emailInfo.samplesNote")}</p>
				{!mid.identities.length && <p className="hint">{t("emailInfo.noIdentities")}</p>}
				{mid.identities.map((identity, index) => <details className="identity" key={`${identity.session_id}:${index}`} open={mid.identities.length === 1 || undefined}>
					<summary><UserRound size={15} aria-hidden="true" /><strong>{identity.name || identity.email || t("emailInfo.identity")}</strong>{identity.session_id && <span className="muted">#{identity.session_id}</span>}<span className="item-sub">{[identity.created_at && date(identity.created_at), identity.countryCode, identity.ip].filter(Boolean).join(" · ")}</span></summary>
					<dl className="fields">
						<div><dt>{t("emailInfo.created_at")} (UTC)</dt><dd>{date(identity.created_at)}</dd></div>
						{emailIdentityFields.map(key => <div key={key}><dt>{t(`emailInfo.${key}`)}</dt><dd className={identity[key] ? undefined : "missing"}>{identity[key] || "—"}</dd></div>)}
					</dl>
				</details>)}
			</div>
		</details>)}
	</section>)}</div>;
}

function Cards({ cards }: { cards: EmailCard[] }) {
	useLocale();
	const { t, count } = useFormat();
	if (!cards.length) return <p className="card empty">{t("emailInfo.noCards")}</p>;
	return <section className="card list" aria-label={t("emailInfo.cards")}>{cards.map((card, index) => <div className="item item-head" key={`${card.token}:${index}`}>
		<CreditCard className="item-icon" size={16} aria-hidden="true" />
		<span className="item-title"><span className="item-name">{card.scheme && <span className="scheme">{card.scheme.toUpperCase()}</span>}<strong>{card.masked || t("emailInfo.unnamedCard")}</strong></span><span className="item-sub"><SeenRange seen={card} zone /></span></span>
		<MiniRate completed={card.completed} attempts={card.attempts} />
		<span className="stat">{t("emailInfo.midsCount", { value: count(card.mids_count) })}</span>
		<Disputes charged={card.charged} />
	</div>)}</section>;
}

export function EmailInfoDetails({ data }: { data: EmailInfoData }) {
	useLocale();
	const { t, count } = useFormat();
	const signals = emailSignals(data.summary);
	const flagged = new Set<string>(signals.map(signal => signal.key));
	return <Content>
		<Verdict summary={data.summary} signals={signals} />

		<SectionLabel>{t("emailInfo.payments")}{data.period.axis === "payment_created_at" && <span className="note">{t("emailInfo.periodBasis")}</span>}</SectionLabel>
		<div className="kpis">
			<Conversion summary={data.summary} />
			{data.amounts.length
				? data.amounts.map((row, index) => <Volume key={`${row.currency}:${index}`} row={row} split={data.amounts.length > 1} />)
				: <article className="card kpi" aria-label={t("emailInfo.volume")}><header className="kpi-head"><Banknote size={16} aria-hidden="true" />{t("emailInfo.volume")}</header><p className="hint" style={{ marginTop: 12 }}>{t("emailInfo.noAmounts")}</p></article>}
		</div>

		<SectionLabel>{t("emailInfo.linked")}</SectionLabel>
		<section className="card linked" aria-label={t("emailInfo.linked")}>
			<ul className="tiles">{linkedMetrics.map(([key, Icon]) => {
				const attention = flagged.has(key);
				return <li className={`tile${attention ? " flagged" : ""}`} key={key}>
					<span className="tile-icon" aria-hidden="true"><Icon size={16} /></span>
					<span className="tile-text"><strong>{count(data.summary[key])}</strong><span>{t(`emailInfo.${key}`)}</span></span>
					{attention && <span className="tile-flag" role="img" title={t("emailInfo.flagged")} aria-label={t("emailInfo.flagged")}><TriangleAlert size={14} aria-hidden="true" /></span>}
				</li>;
			})}</ul>
			<dl className="recipe">
				<dt>{t("emailInfo.names")}</dt>
				<dd>{data.identity_names.length ? <span className="chips">{data.identity_names.map((name, index) => <span className="chip" key={`${name}:${index}`}>{name}</span>)}</span> : <span className="hint">—</span>}</dd>
				<dt>{t("emailInfo.activity")} (UTC)</dt>
				<dd><SeenRange seen={data.summary} /></dd>
			</dl>
		</section>

		<SectionLabel>{t("emailInfo.clients")}<span className="count">{count(data.clients.length)}</span></SectionLabel>
		<Clients clients={data.clients} />

		<SectionLabel>{t("emailInfo.cards")}<span className="count">{count(data.cards.length)}</span></SectionLabel>
		<Cards cards={data.cards} />
	</Content>;
}
